import * as React from 'react';
import { ResponsiveImage } from 'lib/types';
import classNames from 'classnames';
import { AiFillStar, AiOutlineGoogle } from 'react-icons/ai';
import useIsMobile from 'core/hooks/useIsMobile';
import ExternalLink from 'core/components/ExternalLink';
import ReviewsTopSwirl from 'assets/reviews-top-swirl.inline.svg';
import { graphql } from 'gatsby';
import { Swiper, SwiperSlide } from 'swiper/react';
import useHasIntersected from 'core/hooks/useHasIntersected';
import { REVIEW_LINK } from 'locations/lib/types';
import ImageWrapper from 'core/components/ImageWrapper';
import * as styles from './GoogleReviews.module.scss';

type Review = {
    rating: number;
    comment: string;
    reviewer: {
        displayName: string;
    };
};

export const query = graphql`
    fragment GoogleReviewsMobileImageSharp on ImageSharp {
        gatsbyImageData(
            width: 428
            height: 483
            transformOptions: { fit: COVER, cropFocus: SOUTH }
            sizes: "100vw"
            breakpoints: [
                375
                414
                430
                750
                768
                828
                860
                900
                1125
                1242
                1244
                1290
            ]
        )
    }
    fragment GoogleReviewsDesktopImageSharp on ImageSharp {
        gatsbyImageData(
            width: 2500
            height: 1250
            transformOptions: { fit: COVER, cropFocus: CENTER }
            sizes: "100vw"
            breakpoints: [
                768
                828
                900
                1125
                1242
                1244
                1600
                1700
                1900
                2200
                2500
            ]
        )
    }
    fragment GoogleReviewsMobileImage on File {
        publicURL
        alt
        childImageSharp {
            ...GoogleReviewsMobileImageSharp
        }
    }
    fragment GoogleReviewsDesktopImage on File {
        publicURL
        alt
        childImageSharp {
            ...GoogleReviewsDesktopImageSharp
        }
    }
    fragment GoogleReviewsResponsiveImage on File {
        publicURL
        alt
        desktop: childImageSharp {
            ...GoogleReviewsDesktopImageSharp
        }
        mobile: childImageSharp {
            ...GoogleReviewsMobileImageSharp
        }
    }
`;

export type GoogleReviewsProps = {
    image?: ResponsiveImage;
    googlePlaceId?: string;
    reviews: Review[];
    className?: string;
    imageNode?: React.ReactNode;
    imageAlt?: string;
};

const renderReview = (
    { comment, reviewer, rating }: Review,
    placeId?: string,
    key?: string,
) => {
    const stars = [...Array(rating)].map((val, idx) => (
        <AiFillStar key={idx.toString(16)} />
    ));

    return (
        <div className={styles.review} key={key}>
            <div className={styles.starsWrapper}>
                <div aria-hidden="true">{stars}</div>
            </div>
            <div className={styles.comment} tabIndex={0} role="textbox">
                {comment}
            </div>
            <div className={styles.reviewer}>&ndash;{reviewer.displayName}</div>
            {placeId && (
                <ExternalLink
                    href={REVIEW_LINK + placeId}
                    className={styles.seeMore}
                >
                    <AiOutlineGoogle />
                    <span>see more on google</span>
                </ExternalLink>
            )}
        </div>
    );
};

function GoogleReviews({
    image,
    reviews,
    className,
    googlePlaceId,
    imageNode,
    imageAlt,
}: GoogleReviewsProps) {
    const isMobile = useIsMobile();
    const rootRef = React.useRef(null);
    const hasIntersected = useHasIntersected(rootRef);

    const loading = hasIntersected ? 'eager' : 'lazy';

    const reviewsComponent = React.useMemo(() => {
        const filteredReviews = reviews
            ?.filter(
                ({ comment, rating, reviewer }) =>
                    comment && rating && reviewer?.displayName,
            )
            .sort((a, b) => b.rating - a.rating)
            .slice(0, 3);

        if (isMobile && filteredReviews?.length) {
            return (
                <Swiper
                    slidesPerView="auto"
                    spaceBetween={10}
                    loop
                    preloadImages
                    centeredSlides
                    className={styles.swiper}
                >
                    {filteredReviews?.map((review, idx) => (
                        <SwiperSlide
                            key={`${review.rating}_${idx}`}
                            className={styles.swiperSlide}
                        >
                            {renderReview(review, googlePlaceId)}
                        </SwiperSlide>
                    ))}
                </Swiper>
            );
        }
        return filteredReviews?.map((review, idx) =>
            renderReview(review, googlePlaceId, `${review.rating}_${idx}`),
        );
    }, [isMobile, googlePlaceId, reviews]);

    if (!reviewsComponent) {
        return null;
    }

    return (
        <section
            className={classNames(styles.root, className, {
                [styles.noImage]: !image,
            })}
            ref={rootRef}
        >
            {image && (
                <>
                    <div className={styles.topSwirl} aria-hidden="true">
                        <ReviewsTopSwirl />
                    </div>
                    <ImageWrapper
                        alt={imageAlt ?? 'Google Reviews Background'}
                        image={image}
                        className={styles.image}
                        loading={loading}
                    />
                </>
            )}
            {imageNode}
            <div className={styles.content}>{reviewsComponent}</div>
        </section>
    );
}

export default React.memo(GoogleReviews);
